import { ReactNode, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { compact, uniqBy } from "lodash-es";
// Helpers
import {
	displaySkeleton,
	formatAccountDescription,
	formatRowModesModel,
	// formatRowModesModel,
	resolveChargeBackIconColor,
	resolveTextColumnWidth,
	substituteRowsById,
} from "@/Settings/helpers/SystemSettings/appendingConfiguration";
import { getSanitatedLocationOEMs } from "@/Common/helpers/OEMs";

// Components
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";

// MUI
import {
	useGridApiRef,
	GridRowModes,
	GridActionsCellItem,
	GridColDef,
	GridRowSelectionModel,
	GridEventListener,
	GridValidRowModel,
	GridRowModesModel,
} from "@mui/x-data-grid-premium";
import { Box, Tooltip, Typography, Stack } from "@mui/material";

// Icons
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CancelIcon from "@mui/icons-material/Close";
import ArithmeticDialog from "./ArithmeticDialog";
import InputSelectCell from "./InputSelectCell";
import { GlAccount, IGlAccount, IRow, IWSGroup, TValue } from "@/Settings/types/SystemSettings/appendingConfiguration";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { errorLogger } from "@/Common/helpers/logger";
import { excludeGroups } from "@/Settings/Constants/AppendingConfig";
import InfoSubTitle from "../../../common/InfoSubTitle";

const CustomHeader = ({ text, align = "left", width }: { text: string; align?: string; width: number }) => {
	const splitText = text.split(" ");
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				justifyContent: "flex-start",
				height: "45px",
				textAlign: align,
				width,
			}}
		>
			<Typography className="!font-montserrat !text-xs text-default" component="p">
				{splitText[0]}
			</Typography>
			<Typography className="!font-montserrat !text-xs text-default" component="p">
				{splitText[1]}
			</Typography>
		</Box>
	);
};

type TAppendingConfigurationTableProps = {
	hideGroups: boolean;
	franchise?: string | null;
	reference: string;
	isCombined?: boolean;
	wsGroups: IWSGroup[];
	title: ReactNode;
	emptyMessage: string;
	hideOem: boolean;
	groups?: { group: string; subgroups: string[]; franchiseName: string }[];
	data: IRow[];
	isPending?: boolean;
	onSaveConfiguration: (data: { finalRow?: IRow; reference: string; franchise?: string | null }) => Promise<void>;
	onRemoveConfiguration: (ids: string[]) => Promise<void>;
	onMultipleSaveConfiguration: (rows: IRow[]) => Promise<void>;
	resolveGlAccounts: () => { allSelectableGlAccounts: IGlAccount[]; filteredGlAccounts: IGlAccount[] };
};

const AppendingConfigurationTable = (props: TAppendingConfigurationTableProps) => {
	const {
		hideGroups,
		franchise,
		wsGroups,
		reference,
		isCombined,
		groups,
		data,
		title,
		emptyMessage,
		hideOem,
		onSaveConfiguration,
		onRemoveConfiguration,
		onMultipleSaveConfiguration,
		isPending,
		resolveGlAccounts,
	} = props;
	const [oem, setOem] = useState("");
	const [rows, setRows] = useState<IRow[]>(data);
	const [gridKey, setGridKey] = useState(0);
	const [openArithmeticDialog, setOpenArithmeticDialog] = useState(false);
	const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
	const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
	const apiRef = useGridApiRef();
	const isFinanceGross = reference === "financeGross";

	const stringData = JSON.stringify(data);
	useEffect(() => {
		setRows(compact(data));
		setRowModesModel((prev) => formatRowModesModel(prev, { mode: GridRowModes.View, ignoreModifications: true }));
		return () => {
			setRows([]);
		};
	}, [stringData]);

	const editModeActive = useMemo(() => Object.values(rowModesModel)?.some(({ mode }) => mode === GridRowModes.Edit), [rowModesModel]);

	const location = useSelector((state: RootState) => state.location.location);
	const textColumnWidth = resolveTextColumnWidth({ hideGroups });
	const numberColumnWidth = 75;

	const handleCancelClick = (id: string) => {
		setOem("");
		setRowModesModel({
			...rowModesModel,
			[id]: { mode: GridRowModes.View, ignoreModifications: true },
		});

		const editedRow = rows.find((row) => row._id === id);
		if (editedRow?.isNew) {
			setRows(rows.filter((row) => row._id !== id));
		}
	};

	const handleAddRow = () => {
		// define a default row initial values.
		const key = uuidv4();
		const newRow = {
			_id: key,
			isNew: true,
			accountDescription: null,
			glAccount: null,
			fsAccount: null,
			groupInfo: null,
			properties: null,
			arithmeticExpression: { operation: "+", value: 0 },
			oemReference: null,
			dealerCodeFS: null,
		};

		// Add the new row to the rows state.
		setRows((prev) => [newRow, ...prev]);
		// Set the new row in edit mode.
		setRowModesModel((prev) => ({
			...prev,
			[key]: { mode: GridRowModes.Edit },
		}));
	};

	const handleRowSelected = (newSelection: GridRowSelectionModel) => {
		setSelectedRows(newSelection);
	};

	const handleRowEditStart: GridEventListener<"rowEditStart"> = (_params, event) => {
		if (editModeActive) {
			event.defaultPrevented = true;
			event.defaultMuiPrevented = true;
		}
	};

	const handleRowEditStop: GridEventListener<"rowEditStop"> = (_params, event) => {
		event.defaultMuiPrevented = true;
	};

	const handleProcessRowUpdateError = (error: Error) => {
		errorLogger(error);
	};

	const handleDeleteClick = async (ids: string[]) => {
		setRows((prev) => prev.filter((row) => !ids.includes(row._id)));
		setSelectedRows((prev) => prev.filter((id) => !ids.includes(String(id))));
		await onRemoveConfiguration(ids);
	};

	const handleProcessRowUpdate = async (newRow: GridValidRowModel) => {
		const updatedRows = rows.map((item) => {
			if (item._id === newRow._id) {
				const updatedRow = {
					...item,
					groupInfo: { group: newRow.groupInfo?.group, subGroup: newRow.groupInfo?.label },
					accountDescription: newRow.glAccount?.accountDescription || newRow.accountDescription,
					glAccount: [newRow.glAccount?.glAccount],
					fsAccount: [newRow.glAccount?.fsAccount || newRow.fsAccount],
					oemReference: newRow.glAccount?.oemReference || newRow.oemReference,
					dealerCodeFS: newRow.accountDescription?.dealerCodeFS,
				};
				delete updatedRow.isNew;
				return updatedRow;
			}
			return item;
		});

		const finalRow = updatedRows.find((item) => item._id === newRow._id) as IRow;
		await onSaveConfiguration({ finalRow, reference, franchise });
		return finalRow;
	};

	// as unknown as GridValidRowModel

	const handleArithmetic = () => {
		setOpenArithmeticDialog(true);
	};

	const handleCharBack = async (ids: string[]) => {
		displaySkeleton({ ids, apiRef, action: "display" });
		const filteredRows = rows.filter((row) => ids.includes(row._id));
		const someNotChargeback = filteredRows.some((row) => !row?.properties?.isChargeback);
		const mappedRows = filteredRows.map((row) => {
			if (someNotChargeback) {
				return { ...row, properties: { ...row?.properties, isChargeback: true } };
			}
			return { ...row, properties: { ...row?.properties, isChargeback: false } };
		}) as IRow[];
		await onMultipleSaveConfiguration(mappedRows);
		setRows((prev) => substituteRowsById(prev, mappedRows));
		setSelectedRows([]);
		setGridKey((prevKey) => prevKey + 1);
		displaySkeleton({ ids, apiRef, action: "success" });
	};

	const handleSaveOperations = async (editedRows: IRow[]) => {
		const ids = editedRows.map((row) => row._id);
		displaySkeleton({ ids, action: "display", apiRef });
		await onMultipleSaveConfiguration(editedRows);
		setRows((prev) => substituteRowsById(prev, editedRows));
		setSelectedRows([]);
		setGridKey((prevKey) => prevKey + 1);
		displaySkeleton({ ids, action: "success", apiRef });
	};

	const columns: GridColDef[] = [
		{
			field: "_id",
			headerName: "ID",
			width: 5,
			filterable: false,
			sortable: false,
			resizable: false,
		},
		{
			field: "accountDescription",
			headerName: "Account Description",
			width: textColumnWidth,
			resizable: false,
			filterable: false,
			editable: true,
			sortable: false,
			renderHeader: () => <CustomHeader text="Account Description" width={textColumnWidth} />,
			renderEditCell: (params) => {
				const id = String(params?.id);
				const oemReference = params?.row?.oemReference;
				const dealerCodeFS = params?.row?.dealerCodeFS;
				let finalOEMs = [];
				const locationOEMs = getSanitatedLocationOEMs(location?.oems || []);
				const oems = franchise
					? locationOEMs.filter((oem) => oem?.franchises?.some((item: { franchise: string }) => item?.franchise === franchise))
					: locationOEMs;

				if (oemReference) {
					finalOEMs = oems.filter(
						(finalItem) => finalItem.oemReference === oemReference && finalItem.dealerCodeFS === dealerCodeFS
					);
				} else {
					finalOEMs = oems;
				}
				return (
					<InputSelectCell
						list={finalOEMs.map((oem) => ({
							label: oem.oemDisplayName,
							oemReference: oem.oemReference,
							dealerCodeFS: oem.dealerCodeFS,
							version: oem.version,
							fsVersion: oem.financialStatementVersion,
						}))}
						{...params}
						id={id}
						placeholder="Oems"
						setRowModesModel={setRowModesModel}
						hideGroups={hideGroups}
						setOem={setOem}
						oem={oem}
					/>
				);
			},
			valueFormatter: (_: unknown, row) => {
				const newRow = formatAccountDescription(row);
				return newRow.accountDescription;
			},
		},
		{
			field: "groupInfo",
			headerName: "Group",
			width: 100,
			resizable: false,
			filterable: false,
			editable: true,
			sortable: false,
			renderHeader: () => <CustomHeader text="Group" width={100} />,
			renderEditCell: (params) => {
				const id = String(params?.id);
				const { oemReference, version, fsVersion } = params?.row?.accountDescription || {};
				const foundWsGroups = wsGroups?.find(
					(groupItem) =>
						groupItem?.section === reference &&
						groupItem?.oemReference === oemReference &&
						groupItem?.version === version &&
						groupItem?.fsVersion === fsVersion
				);
				let mappedGroups;
				if (foundWsGroups?.section === "newCarsSales") {
					let firstGroupFranchise;
					if (isCombined) {
						firstGroupFranchise = groups?.[0]?.franchiseName;
					} else {
						firstGroupFranchise = franchise;
					}
					mappedGroups = foundWsGroups?.franchises
						?.find((franchiseItem) => franchiseItem.franchise.includes(firstGroupFranchise ?? ""))
						?.groups?.map((groupItem) => {
							return groupItem?.subgroups?.map((subgroupItem) => {
								return { group: groupItem.group, label: subgroupItem };
							});
						})
						?.flat(Infinity);
				} else {
					mappedGroups = foundWsGroups?.groups
						?.map((groupItem) => {
							return groupItem?.subgroups?.map((subgroupItem: string) => {
								return { group: groupItem.group, label: subgroupItem };
							});
						})
						.flat(Infinity);
				}
				return (
					<InputSelectCell
						tooltipText="Please select an OEM first"
						disabled={!foundWsGroups}
						list={mappedGroups as TValue[]}
						{...params}
						id={id}
						placeholder="Groups"
						setRowModesModel={setRowModesModel}
						hideGroups={hideGroups}
						setOem={setOem}
						oem={oem}
					/>
				);
			},
			valueFormatter: (value: { subGroup: string }) => value?.subGroup || "",
		},
		{
			field: "glAccount",
			headerName: "GL Acct#",
			width: 100,
			filterable: false,
			resizable: false,
			editable: true,
			sortable: false,
			align: "right",
			renderHeader: () => <CustomHeader text="GL Acct#" align="right" width={100} />,
			renderEditCell: (params) => {
				const id = String(params?.id);
				const accountDescription = rows.find((row) => row._id === id)?.accountDescription as string;
				const rowGlAccount = (params?.row?.glAccount || [])[0]?.glAccount;
				const { allSelectableGlAccounts, filteredGlAccounts } = resolveGlAccounts();
				const glAccounts = excludeGroups.includes(reference) ? allSelectableGlAccounts : filteredGlAccounts;
				if (rowGlAccount) {
					glAccounts.push({
						label: rowGlAccount as string,
						oemReference: params?.row?.accountDescription?.oemReference as string,
						dealerCodeFS: params?.row?.accountDescription?.dealerCodeFS as string,
						fsAccount: (params?.row?.fsAccount || [])[0] as string,
						glAccount: params?.row?.glAccount as GlAccount,
						accountDescription,
					});
				}
				const finalGlAccounts = uniqBy(glAccounts, "label")?.filter(
					(glAccount) => glAccount?.oemReference === params?.row?.accountDescription?.oemReference
				);

				return (
					<InputSelectCell
						tooltipText="Please select an OEM first"
						disabled={!finalGlAccounts?.length}
						list={finalGlAccounts}
						{...params}
						id={id}
						placeholder="GL"
						setRowModesModel={setRowModesModel}
						hideGroups={hideGroups}
						setOem={setOem}
						oem={oem}
					/>
				);
			},
			valueFormatter: (value: { glAccount: string }[]) => value?.[0]?.glAccount,
		},
		{
			field: "fsAccount",
			headerName: "FS Acct#",
			width: numberColumnWidth,
			resizable: false,
			filterable: false,
			sortable: false,
			align: "right",
			renderHeader: () => <CustomHeader text="FS Acct#" align="right" width={numberColumnWidth} />,
		},
		{
			field: "info",
			headerName: "",
			minWidth: 50,
			maxWidth: 50,
			filterable: false,
			editable: true,
			sortable: false,
			resizable: false,
			cellClassName: "info-small-cell",
			renderCell: (params) => {
				const isChargeback = params?.row?.properties?.isChargeback;
				if (!hideOem) {
					const locationOEMs = getSanitatedLocationOEMs(location?.oems || []);
					const oemName = locationOEMs.find(
						(oemItem) =>
							oemItem?.oemReference === params?.row?.oemReference && oemItem?.dealerCodeFS === params?.row?.dealerCodeFS
					)?.oemDisplayName;
					return (
						<Tooltip
							title={
								<Typography sx={{ fontFamily: "'PTSansProNarrow', sans-serif", fontSize: "14px" }} component="span">
									{oemName}
								</Typography>
							}
							placement={isFinanceGross && isChargeback ? "left" : "right"}
							arrow
						>
							<InfoOutlinedIcon fontSize="small" sx={{ marginLeft: "10px" }} />
						</Tooltip>
					);
				}
				return null;
			},
			renderEditCell: (params) => {
				return (
					<GridActionsCellItem
						key="cancel"
						icon={<CancelIcon />}
						label="Cancel"
						className="textPrimary"
						onClick={() => handleCancelClick(String(params?.id))}
						color="inherit"
						sx={{ marginLeft: "5px" }}
					/>
				);
			},
		},
		{
			field: "chargeback",
			headerName: "",
			minWidth: 25,
			maxWidth: 25,
			align: "right",
			resizable: false,
			filterable: false,
			sortable: false,
			renderCell: (params) => {
				const isChargeback = params?.row?.properties?.isChargeback;
				return (
					isFinanceGross &&
					isChargeback && (
						<Tooltip
							title={
								<Typography sx={{ fontFamily: "'PTSansProNarrow', sans-serif", fontSize: "14px" }} component="span">
									Chargeback
								</Typography>
							}
							placement="right"
							arrow
						>
							<Stack
								style={{
									background: "var(--text-error-color)",
									width: "5px",
									height: "100%",
									position: "relative",
									right: "-10px",
								}}
							/>
						</Tooltip>
					)
				);
			},
		},
	];

	const autoHeight = rows.length < 10;
	const tableSx = autoHeight ? { width: "510px", height: "100%" } : { width: "510px", height: 385 };

	return (
		<Box style={{ width: "100%" }}>
			<DataSmartTable
				sx={tableSx}
				disableColumnSorting
				disableColumnReorder
				autoHeight={autoHeight}
				headerHeight={45}
				columns={columns}
				rows={rows}
				rowHeight={30}
				apiRef={apiRef}
				editMode="row"
				disableColumnMenu
				disableRowSelectionOnClick
				checkboxSelection
				isRowSelectable={() => !(editModeActive || isPending)}
				onRowSelectionModelChange={handleRowSelected}
				rowModesModel={rowModesModel}
				processRowUpdate={handleProcessRowUpdate}
				onProcessRowUpdateError={handleProcessRowUpdateError}
				onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
				onRowEditStart={handleRowEditStart}
				onRowEditStop={handleRowEditStop}
				columnVisibilityModel={{
					_id: false,
					groupInfo: !hideGroups,
				}}
				slots={{
					toolbar: CommonToolbar,
					noRowsOverlay: NoRowsOverlay,
				}}
				slotProps={{
					toolbar: {
						customTitle: {
							hide: false,
							label: typeof title === "string" ? <InfoSubTitle title={title} /> : title,
						},
						add: {
							onAddAction: handleAddRow,
							hide: selectedRows.length,
							disabled: editModeActive || isPending,
						},
						arithmetic: {
							onArithmeticAction: handleArithmetic,
							disabled: isPending,
							hide: !selectedRows.length,
						},
						chargeBack: {
							onChargeBackAction: handleCharBack,
							disabled: isPending,
							hide: !selectedRows.length || !isFinanceGross,
							color: resolveChargeBackIconColor({ rows, selectedRows }),
						},
						delete: {
							onDeleteAction: handleDeleteClick,
							deleteTitle: "Remove Account",
							confirmationButtonClasses: "!w-[140px] hover:!border-gray-blue hover:!shadow-gray-blue",
							hide: !selectedRows.length,
							disabled: isPending,
						},
					},
					noRowsOverlay: {
						title: `No ${emptyMessage} Data Found`,
					},
				}}
				key={gridKey}
				hideFooter
				customProps={{
					tableContainerStyle: { height: "100%", width: "100%" },
					tableClassName: {
						"functional-table": true,
						"edit-table": true,
					},
				}}
			/>
			<ArithmeticDialog
				openArithmeticDialog={openArithmeticDialog}
				setOpenArithmeticDialog={setOpenArithmeticDialog}
				rows={rows}
				selectedRows={selectedRows}
				onSaveOperations={handleSaveOperations}
			/>
		</Box>
	);
};

export default AppendingConfigurationTable;
